import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Title, useGetList, useNotify, usePermissions, useRedirect } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { apiErrorMessage, todayLocalYmd } from "../shared/api-error";
import { formatRupees } from "../shared/fee-format";
import { MADRASA_FEES_CREATE } from "../shared/madrasa-permissions";
import { StudentPicker, type StudentOption } from "../shared/StudentPicker";
import {
  allocateOldestFirst,
  isOpenDue,
  PAYMENT_MODES,
  selectAll,
  selectionProblems,
  selectionTotal,
  sortOldestFirst,
  toAllocations,
  validateCollectForm,
  type DueLite,
  type PaymentMode,
  type Selection,
} from "./collect-fee-utils";

type YearRow = { id: string; name: string; isCurrent: boolean; isClosed: boolean };

/** Collect a fee: find the student, tick what they are paying, take the payment, get a receipt. */
export default function CollectFeePage() {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const redirect = useRedirect();
  const canCollect = hasPermission(permissions, MADRASA_FEES_CREATE);

  const years = useGetList<YearRow>("academicYears", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "startDate", order: "DESC" },
  });
  const [yearId, setYearId] = useState("");
  useEffect(() => {
    if (!yearId && years.data?.length)
      setYearId((years.data.find((y) => y.isCurrent) ?? years.data[0]).id);
  }, [years.data, yearId]);

  const [student, setStudent] = useState<StudentOption | null>(null);
  const [dues, setDues] = useState<DueLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState<Selection>({});
  const [received, setReceived] = useState("");
  const [mode, setMode] = useState<PaymentMode>("CASH");
  const [reference, setReference] = useState("");
  const [date, setDate] = useState(todayLocalYmd());
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const loadDues = useCallback(async () => {
    if (!student || !yearId) {
      setDues([]);
      return;
    }
    setLoading(true);
    try {
      const r = await callApi({
        location: "feeDues",
        method: "GET",
        data: { studentId: student.id, academicYearId: yearId, limit: 1000 },
      });
      setDues(((r.data as { rows?: DueLite[] }).rows ?? []) as DueLite[]);
      setSelection({});
      setReceived("");
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Could not load the dues"), { type: "error" });
      setDues([]);
    } finally {
      setLoading(false);
    }
  }, [student, yearId, notify]);

  useEffect(() => {
    void loadDues();
  }, [loadDues]);

  const open = useMemo(() => sortOldestFirst(dues.filter(isOpenDue)), [dues]);
  const outstanding = open.reduce((n, d) => n + d.outstanding, 0);
  const total = selectionTotal(selection);
  const problems = selectionProblems(dues, selection);
  const today = todayLocalYmd();
  const formProblem = validateCollectForm({ mode, reference, date, today, dues, selection });

  const toggle = (due: DueLite, on: boolean) =>
    setSelection((current) => {
      const next = { ...current };
      if (on) next[due.id] = String(due.outstanding);
      else delete next[due.id];
      return next;
    });

  const allocate = () => {
    const amount = Number(received);
    const { selection: next, unallocated } = allocateOldestFirst(dues, amount);
    setSelection(next);
    if (unallocated > 0) {
      notify(`${formatRupees(unallocated)} is more than is due, so it was not allocated.`, {
        type: "warning",
      });
    }
  };

  const submit = async () => {
    if (!student || formProblem) return;
    setSaving(true);
    try {
      const response = await callApi({
        location: "feeReceipts",
        method: "POST",
        data: {
          studentId: student.id,
          academicYearId: yearId,
          receiptDate: date,
          paymentMode: mode,
          ...(reference.trim() ? { paymentReference: reference.trim() } : {}),
          ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
          allocations: toAllocations(selection),
        },
      });
      const receipt = (response.data as { rows: Array<{ id: string; receiptNo: string }> }).rows[0];
      notify(`Receipt ${receipt.receiptNo} issued.`, { type: "success" });
      redirect("show", "feeReceipts", receipt.id);
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Could not save the payment"), { type: "error" });
      void loadDues(); // a due may have been paid by someone else: show the current state
    } finally {
      setSaving(false);
    }
  };

  if (!canCollect) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">You do not have permission to collect fees.</Alert>
      </Box>
    );
  }

  return (
    <>
      <Title title="Collect fee" />
      <Box sx={{ p: 2, maxWidth: 980 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Collect fee
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Find the student, choose what they are paying, and issue a receipt.
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: { sm: 320 } }}>
            <StudentPicker value={student} onChange={setStudent} autoFocus />
          </Box>
          <TextField
            select
            label="Academic year"
            value={yearId}
            onChange={(e) => setYearId(e.target.value)}
            sx={{ width: { sm: 220 }, flexShrink: 0 }}
          >
            {(years.data ?? []).map((y) => (
              <MenuItem key={y.id} value={y.id}>
                {y.name}
                {y.isCurrent ? " (current)" : ""}
                {y.isClosed ? " (closed)" : ""}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {student && loading && <CircularProgress size={28} />}

        {student && !loading && open.length === 0 && (
          <Alert severity={dues.length ? "success" : "info"}>
            {dues.length
              ? "Nothing is due for this student in this academic year."
              : "No dues have been generated for this student in this year. Check that they are enrolled and that fees are set and synced."}
          </Alert>
        )}

        {student && !loading && open.length > 0 && (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ alignItems: { sm: "center" } }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  Outstanding: <strong>{formatRupees(outstanding)}</strong> across {open.length}{" "}
                  {open.length === 1 ? "due" : "dues"}
                </Typography>
                <TextField
                  size="small"
                  label="Amount received"
                  value={received}
                  onChange={(e) => setReceived(e.target.value)}
                  slotProps={{ htmlInput: { inputMode: "numeric" } }}
                  sx={{ width: 170 }}
                />
                <Button onClick={allocate} disabled={!/^\d+$/.test(received.trim())}>
                  Pay oldest first
                </Button>
                <Button onClick={() => setSelection(selectAll(dues))}>Select all</Button>
                <Button
                  onClick={() => setSelection({})}
                  disabled={Object.keys(selection).length === 0}
                >
                  Clear
                </Button>
              </Stack>
            </Paper>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 380 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Fee</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Due date</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    <TableCell align="right" sx={{ width: 150 }}>
                      Paying now
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {open.map((d) => {
                    const ticked = d.id in selection;
                    return (
                      <TableRow key={d.id} hover selected={ticked}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={ticked}
                            onChange={(_, on) => toggle(d, on)}
                            slotProps={{
                              input: { "aria-label": `Pay ${d.feeHeadName} ${d.periodLabel}` },
                            }}
                          />
                        </TableCell>
                        <TableCell>{d.feeHeadName}</TableCell>
                        <TableCell>{d.periodLabel}</TableCell>
                        <TableCell>
                          {d.dueDate}
                          {d.overdue && (
                            <Chip size="small" color="error" label="Overdue" sx={{ ml: 1 }} />
                          )}
                          {d.status === "PARTIAL" && (
                            <Chip size="small" color="warning" label="Part paid" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell align="right">{formatRupees(d.outstanding)}</TableCell>
                        <TableCell align="right">
                          {ticked && (
                            <TextField
                              size="small"
                              value={selection[d.id]}
                              onChange={(e) =>
                                setSelection((cur) => ({ ...cur, [d.id]: e.target.value }))
                              }
                              error={Boolean(problems[d.id])}
                              title={problems[d.id]}
                              slotProps={{
                                htmlInput: {
                                  inputMode: "numeric",
                                  style: { textAlign: "right" },
                                  "aria-label": `Amount for ${d.periodLabel}`,
                                },
                              }}
                              sx={{ width: 110 }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    select
                    label="Paid by"
                    value={mode}
                    onChange={(e) => setMode(e.target.value as PaymentMode)}
                    sx={{ minWidth: 180 }}
                  >
                    {PAYMENT_MODES.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label={mode === "CASH" ? "Reference (optional)" : "Reference (required)"}
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    helperText={mode === "CASH" ? " " : "UTR, cheque number or UPI reference"}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Receipt date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
                    sx={{ width: 180 }}
                  />
                </Stack>
                <TextField
                  label="Remarks (optional)"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  fullWidth
                />
                <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={submit}
                    disabled={saving || Boolean(formProblem)}
                  >
                    {saving ? "Saving…" : `Collect ${formatRupees(total)} and issue receipt`}
                  </Button>
                  {formProblem && Object.keys(selection).length > 0 && (
                    <Typography variant="body2" color="error">
                      {formProblem}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </>
        )}
      </Box>
    </>
  );
}
